<script setup>
import { ref, computed } from 'vue'
import { useRouter } from 'vue-router'
import { useAuthStore } from '@/store/auth.js'
import { useEquipmentsStore } from '@/store/equipments.js'

const router = useRouter()
const auth = useAuthStore()
const store = useEquipmentsStore()

// ── Stats (animated counters) ──────────────────────────────────────────────
const stats = computed(() => [
  { icon: 'pi pi-box',       label: 'Items Listed',     value: store.totalItems || 200,  suffix: '+' },
  { icon: 'pi pi-users',     label: 'Happy Renters',    value: 1500,                     suffix: '+' },
  { icon: 'pi pi-map-marker',label: 'Cities Covered',   value: store.cities?.length || 25, suffix: '+' },
  { icon: 'pi pi-star',      label: 'Average Rating',   value: 4.8,                      suffix: '/5' },
])

// ── How it Works tabs ──────────────────────────────────────────────────────
const activeTab = ref('rent')

const rentSteps = [
  { icon: 'pi pi-search',       title: 'Search',         desc: 'Browse thousands of items near you by category, keyword, or location.' },
  { icon: 'pi pi-shopping-cart', title: 'Book & Pay',     desc: 'Add to cart, choose your rental period, and pay securely online.' },
  { icon: 'pi pi-box',          title: 'Pick Up & Use',  desc: 'Coordinate pickup with the owner, use the item, and return it on time.' },
  { icon: 'pi pi-star',         title: 'Rate & Review',  desc: 'Leave a review to help the community and build your renter profile.' },
]

const leaseSteps = [
  { icon: 'pi pi-camera',   title: 'List Your Item',   desc: 'Snap a few photos, set your daily, weekly, or monthly rate, and publish.' },
  { icon: 'pi pi-bell',     title: 'Get Bookings',     desc: 'Receive booking requests with notifications — accept or decline instantly.' },
  { icon: 'pi pi-wallet',   title: 'Earn Money',       desc: 'Get paid securely for every rental. Track your earnings in your dashboard.' },
  { icon: 'pi pi-chart-bar',title: 'Grow Your Business',desc: 'Use insights and stats to optimize your listings and maximize income.' },
]

const currentSteps = computed(() => activeTab.value === 'rent' ? rentSteps : leaseSteps)

// ── Trust pillars ──────────────────────────────────────────────────────────
const trustItems = [
  { icon: 'pi pi-shield',   title: 'Verified Users',      desc: 'Every user goes through identity verification for a safe community.' },
  { icon: 'pi pi-lock',     title: 'Secure Payments',     desc: 'All transactions are encrypted and processed through trusted gateways.' },
  { icon: 'pi pi-comments', title: 'In-App Messaging',    desc: 'Communicate directly with owners or renters — no need to share personal info.' },
  { icon: 'pi pi-star',     title: 'Ratings & Reviews',   desc: 'Transparent feedback system so you always know who you\'re dealing with.' },
]

// ── Tips ───────────────────────────────────────────────────────────────────
const tips = [
  { icon: 'pi pi-calendar',     title: 'Book in Advance',           desc: 'Popular items go fast — book early to guarantee availability and better rates.' },
  { icon: 'pi pi-camera',       title: 'Read the Listing Carefully',desc: 'Check photos, descriptions, and reviews before booking to avoid surprises.' },
  { icon: 'pi pi-map-marker',   title: 'Choose Nearby Items',       desc: 'Renting locally saves time and delivery costs. Use the location filter.' },
  { icon: 'pi pi-credit-card',  title: 'Compare Pricing Tiers',     desc: 'Weekly and monthly rates often offer significant savings over daily pricing.' },
  { icon: 'pi pi-comment',      title: 'Message the Owner',         desc: 'Have questions? Message the owner before booking to clarify pickup, condition, etc.' },
  { icon: 'pi pi-check-circle', title: 'Leave a Review',            desc: 'Help the community by sharing your experience after every rental.' },
]

// ── CTA actions ────────────────────────────────────────────────────────────
const goToBrowse = () => router.push('/browse')

const goToListItem = () => {
  if (!auth.isAuthenticated) {
    router.push('/login')
    return
  }
  if (auth.user?.role !== 'lessor') {
    showRoleSwitch.value = true
    return
  }
  router.push('/list-item')
}

const goToRent = () => {
  if (!auth.isAuthenticated) {
    router.push('/login')
    return
  }
  if (auth.user?.role !== 'lessee') {
    showRoleSwitch.value = true
    return
  }
  router.push('/browse')
}

// ── Role switch modal ──────────────────────────────────────────────────────
const showRoleSwitch = ref(false)
const isSwitching = ref(false)

const switchRole = async () => {
  isSwitching.value = true
  try {
    await auth.updateUserRole()
    showRoleSwitch.value = false
  } finally {
    isSwitching.value = false
  }
}

const targetRoleName = computed(() => {
  if (!auth.user) return ''
  return auth.user.role === 'lessor' ? 'Renter' : 'Owner / Lessor'
})

const currentRoleName = computed(() => {
  if (!auth.user) return ''
  return auth.user.role === 'lessor' ? 'Owner / Lessor' : 'Renter'
})
</script>

<template>
  <!-- ═══ 1. PLATFORM STATS ═══ -->
  <section class="py-10 sm:py-14">
    <div class="max-w-5xl mx-auto px-4">
      <div class="grid grid-cols-2 md:grid-cols-4 gap-4 sm:gap-6">
        <div
          v-for="(stat, i) in stats"
          :key="i"
          class="relative bg-theme-card border border-theme-border rounded-2xl p-5 sm:p-6 text-center group hover:border-primary-500/40 hover:shadow-lg transition-all duration-300"
        >
          <div class="w-12 h-12 mx-auto mb-3 rounded-xl bg-primary-600/10 flex items-center justify-center group-hover:bg-primary-600/20 transition-colors">
            <i :class="[stat.icon, 'text-xl text-primary-600']"></i>
          </div>
          <p class="text-2xl sm:text-3xl font-extrabold text-theme-text leading-none">
            {{ typeof stat.value === 'number' && stat.value % 1 !== 0 ? stat.value.toFixed(1) : stat.value }}<span class="text-primary-600 text-lg sm:text-xl">{{ stat.suffix }}</span>
          </p>
          <p class="text-xs sm:text-sm text-theme-muted mt-1 font-medium">{{ stat.label }}</p>
        </div>
      </div>
    </div>
  </section>

  <!-- ═══ 2. HOW IT WORKS ═══ -->
  <section class="py-10 sm:py-14 bg-theme-bg2/50">
    <div class="max-w-5xl mx-auto px-4">
      <!-- Header + Tabs -->
      <div class="text-center mb-8">
        <h2 class="text-2xl sm:text-3xl font-extrabold text-theme-text">How It Works</h2>
        <p class="text-sm sm:text-base text-theme-muted mt-1.5 max-w-lg mx-auto">
          Whether you're renting or leasing — it's just a few simple steps.
        </p>
        <div class="inline-flex items-center mt-5 bg-theme-card border border-theme-border rounded-full p-1 shadow-sm">
          <button
            @click="activeTab = 'rent'"
            class="px-5 py-2 rounded-full text-sm font-semibold transition-all duration-200"
            :class="activeTab === 'rent'
              ? 'bg-primary-600 text-white shadow-sm'
              : 'text-theme-text2 hover:text-theme-text'"
          >
            <i class="pi pi-shopping-cart mr-1.5 text-xs"></i> I Want to Rent
          </button>
          <button
            @click="activeTab = 'lease'"
            class="px-5 py-2 rounded-full text-sm font-semibold transition-all duration-200"
            :class="activeTab === 'lease'
              ? 'bg-primary-600 text-white shadow-sm'
              : 'text-theme-text2 hover:text-theme-text'"
          >
            <i class="pi pi-dollar mr-1.5 text-xs"></i> I Want to Earn
          </button>
        </div>
      </div>

      <!-- Steps grid -->
      <transition name="fade-slide" mode="out-in">
        <div :key="activeTab" class="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 sm:gap-5">
          <div
            v-for="(step, idx) in currentSteps"
            :key="step.title"
            class="relative bg-theme-card border border-theme-border rounded-2xl p-5 sm:p-6 hover:border-primary-500/40 hover:shadow-lg transition-all duration-300 group"
          >
            <!-- Step number -->
            <span class="absolute -top-3 -left-2 w-7 h-7 rounded-full bg-primary-600 text-white text-xs font-bold flex items-center justify-center shadow">
              {{ idx + 1 }}
            </span>
            <div class="w-11 h-11 mb-3 rounded-xl bg-primary-600/10 flex items-center justify-center group-hover:bg-primary-600/20 transition-colors">
              <i :class="[step.icon, 'text-lg text-primary-600']"></i>
            </div>
            <h3 class="font-bold text-theme-text text-sm sm:text-base mb-1">{{ step.title }}</h3>
            <p class="text-xs sm:text-sm text-theme-muted leading-relaxed">{{ step.desc }}</p>
          </div>
        </div>
      </transition>

      <!-- CTA under steps -->
      <div class="flex flex-col sm:flex-row items-center justify-center gap-3 mt-8">
        <button
          v-if="activeTab === 'rent'"
          @click="goToRent"
          class="inline-flex items-center gap-2 px-7 py-3 rounded-full bg-primary-600 hover:bg-primary-500 text-white font-semibold text-sm transition-all duration-200 shadow-sm hover:shadow active:scale-95"
        >
          <i class="pi pi-search text-xs"></i> Start Renting Now
        </button>
        <button
          v-else
          @click="goToListItem"
          class="inline-flex items-center gap-2 px-7 py-3 rounded-full bg-primary-600 hover:bg-primary-500 text-white font-semibold text-sm transition-all duration-200 shadow-sm hover:shadow active:scale-95"
        >
          <i class="pi pi-plus-circle text-xs"></i> List Your First Item
        </button>
        <router-link
          to="/faqs"
          class="inline-flex items-center gap-2 px-7 py-3 rounded-full border-2 border-theme-border text-theme-text2 hover:border-primary-500 hover:text-primary-600 font-semibold text-sm transition-all duration-200"
        >
          <i class="pi pi-question-circle text-xs"></i> Have Questions?
        </router-link>
      </div>
    </div>
  </section>

  <!-- ═══ 3. DUAL CTA — Rent / Lease ═══ -->
  <section class="py-10 sm:py-14">
    <div class="max-w-5xl mx-auto px-4">
      <div class="grid grid-cols-1 md:grid-cols-2 gap-5">
        <!-- Rent CTA -->
        <div class="relative overflow-hidden bg-gradient-to-br from-primary-600 to-primary-700 rounded-2xl p-6 sm:p-8 text-white group">
          <div class="absolute -top-6 -right-6 w-28 h-28 rounded-full bg-white/5 group-hover:scale-150 transition-transform duration-500"></div>
          <div class="absolute -bottom-4 -left-4 w-20 h-20 rounded-full bg-white/5"></div>
          <div class="relative z-10">
            <div class="w-12 h-12 rounded-xl bg-white/15 flex items-center justify-center mb-4">
              <i class="pi pi-shopping-cart text-xl"></i>
            </div>
            <h3 class="text-xl sm:text-2xl font-extrabold mb-2">Need Something?</h3>
            <p class="text-sm text-white/80 leading-relaxed mb-5 max-w-xs">
              Why buy when you can rent? Find tools, electronics, vehicles, and more — all available near you at a fraction of the price.
            </p>
            <button
              @click="goToBrowse"
              class="inline-flex items-center gap-2 px-6 py-2.5 rounded-full bg-white text-primary-700 font-semibold text-sm hover:bg-neutral-100 transition-all duration-200 shadow active:scale-95"
            >
              Browse Items <i class="pi pi-arrow-right text-xs"></i>
            </button>
          </div>
        </div>

        <!-- Lease CTA -->
        <div class="relative overflow-hidden bg-gradient-to-br from-neutral-800 to-neutral-900 dark:from-neutral-700 dark:to-neutral-800 rounded-2xl p-6 sm:p-8 text-white group">
          <div class="absolute -top-6 -right-6 w-28 h-28 rounded-full bg-secondary-500/10 group-hover:scale-150 transition-transform duration-500"></div>
          <div class="absolute -bottom-4 -left-4 w-20 h-20 rounded-full bg-secondary-500/5"></div>
          <div class="relative z-10">
            <div class="w-12 h-12 rounded-xl bg-secondary-500/20 flex items-center justify-center mb-4">
              <i class="pi pi-dollar text-xl text-secondary-400"></i>
            </div>
            <h3 class="text-xl sm:text-2xl font-extrabold mb-2">Have Items to Rent Out?</h3>
            <p class="text-sm text-white/70 leading-relaxed mb-5 max-w-xs">
              Turn your idle equipment into extra income. List for free, set your own prices, and start earning today.
            </p>
            <button
              @click="goToListItem"
              class="inline-flex items-center gap-2 px-6 py-2.5 rounded-full bg-secondary-500 text-neutral-900 font-semibold text-sm hover:bg-yellow-400 transition-all duration-200 shadow active:scale-95"
            >
              Start Listing <i class="pi pi-arrow-right text-xs"></i>
            </button>
          </div>
        </div>
      </div>
    </div>
  </section>

  <!-- ═══ 4. RENTING TIPS ═══ -->
  <section class="py-10 sm:py-14 bg-theme-bg2/50">
    <div class="max-w-5xl mx-auto px-4">
      <div class="text-center mb-8">
        <h2 class="text-2xl sm:text-3xl font-extrabold text-theme-text">Tips for a Great Experience</h2>
        <p class="text-sm sm:text-base text-theme-muted mt-1.5 max-w-lg mx-auto">
          Make the most of every rental with these quick tips.
        </p>
      </div>
      <div class="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-5">
        <div
          v-for="(tip, i) in tips"
          :key="i"
          class="bg-theme-card border border-theme-border rounded-2xl p-5 hover:border-primary-500/40 hover:shadow-lg transition-all duration-300 group"
        >
          <div class="flex items-start gap-3.5">
            <div class="w-10 h-10 rounded-xl bg-primary-600/10 flex items-center justify-center shrink-0 group-hover:bg-primary-600/20 transition-colors">
              <i :class="[tip.icon, 'text-lg text-primary-600']"></i>
            </div>
            <div>
              <h3 class="font-bold text-theme-text text-sm sm:text-base mb-0.5">{{ tip.title }}</h3>
              <p class="text-xs sm:text-sm text-theme-muted leading-relaxed">{{ tip.desc }}</p>
            </div>
          </div>
        </div>
      </div>
    </div>
  </section>

  <!-- ═══ 5. TRUST & SAFETY ═══ -->
  <section class="py-10 sm:py-14">
    <div class="max-w-5xl mx-auto px-4">
      <div class="text-center mb-8">
        <h2 class="text-2xl sm:text-3xl font-extrabold text-theme-text">Built on Trust & Safety</h2>
        <p class="text-sm sm:text-base text-theme-muted mt-1.5 max-w-lg mx-auto">
          Your security is our priority. Here's how we keep you protected.
        </p>
      </div>
      <div class="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 sm:gap-5">
        <div
          v-for="(item, i) in trustItems"
          :key="i"
          class="text-center bg-theme-card border border-theme-border rounded-2xl p-5 sm:p-6 hover:border-primary-500/40 hover:shadow-lg transition-all duration-300 group"
        >
          <div class="w-14 h-14 mx-auto mb-3.5 rounded-2xl bg-primary-600/10 flex items-center justify-center group-hover:bg-primary-600 group-hover:text-white transition-all duration-300">
            <i :class="[item.icon, 'text-2xl text-primary-600 group-hover:text-white transition-colors duration-300']"></i>
          </div>
          <h3 class="font-bold text-theme-text text-sm sm:text-base mb-1">{{ item.title }}</h3>
          <p class="text-xs sm:text-sm text-theme-muted leading-relaxed">{{ item.desc }}</p>
        </div>
      </div>
    </div>
  </section>

  <!-- ═══ 6. BOTTOM CTA BANNER ═══ -->
  <section class="py-10 sm:py-14">
    <div class="max-w-5xl mx-auto px-4">
      <div class="relative overflow-hidden bg-gradient-to-r from-primary-600 via-primary-700 to-primary-800 rounded-2xl sm:rounded-3xl p-8 sm:p-12 text-center text-white">
        <!-- Decorative elements -->
        <div class="absolute top-0 left-0 w-full h-full overflow-hidden pointer-events-none">
          <div class="absolute -top-10 -left-10 w-40 h-40 bg-white/5 rounded-full"></div>
          <div class="absolute -bottom-8 -right-8 w-32 h-32 bg-white/5 rounded-full"></div>
          <div class="absolute top-1/2 left-1/4 w-2 h-2 bg-white/20 rounded-full"></div>
          <div class="absolute top-1/3 right-1/3 w-3 h-3 bg-white/10 rounded-full"></div>
        </div>

        <div class="relative z-10 max-w-xl mx-auto">
          <h2 class="text-2xl sm:text-3xl lg:text-4xl font-extrabold mb-3 leading-tight">
            Ready to Get Started?
          </h2>
          <p class="text-sm sm:text-base text-white/80 mb-6 leading-relaxed">
            Join thousands of people renting and earning on Usenlease. It's free to sign up, and takes less than a minute.
          </p>

          <div class="flex flex-col sm:flex-row items-center justify-center gap-3">
            <button
              v-if="!auth.isAuthenticated"
              @click="router.push('/login')"
              class="inline-flex items-center gap-2 px-8 py-3 rounded-full bg-white text-primary-700 font-bold text-sm hover:bg-neutral-100 transition-all duration-200 shadow-lg active:scale-95"
            >
              <i class="pi pi-user-plus text-xs"></i> Sign Up Free
            </button>
            <button
              v-else
              @click="goToBrowse"
              class="inline-flex items-center gap-2 px-8 py-3 rounded-full bg-white text-primary-700 font-bold text-sm hover:bg-neutral-100 transition-all duration-200 shadow-lg active:scale-95"
            >
              <i class="pi pi-search text-xs"></i> Explore Items
            </button>
            <button
              @click="goToListItem"
              class="inline-flex items-center gap-2 px-8 py-3 rounded-full border-2 border-white/30 text-white font-bold text-sm hover:bg-white/10 transition-all duration-200 active:scale-95"
            >
              <i class="pi pi-plus-circle text-xs"></i> List an Item
            </button>
          </div>
        </div>
      </div>
    </div>
  </section>

  <!-- ═══ ROLE SWITCH MODAL ═══ -->
  <teleport to="body">
    <transition name="role-modal">
      <div
        v-if="showRoleSwitch"
        class="fixed inset-0 z-[70] flex items-center justify-center p-4"
        @click.self="showRoleSwitch = false"
      >
        <div class="absolute inset-0 bg-black/50 backdrop-blur-sm"></div>

        <div class="relative w-full max-w-sm bg-theme-card rounded-2xl shadow-2xl border border-theme-border p-6 flex flex-col items-center text-center gap-4">
          <div class="w-14 h-14 rounded-full bg-primary-600/10 flex items-center justify-center">
            <i class="pi pi-sync text-2xl text-primary-600"></i>
          </div>

          <div>
            <h3 class="text-lg font-bold text-theme-text mb-1">Switch Your Role?</h3>
            <p class="text-sm text-theme-muted leading-relaxed">
              You're currently signed in as
              <span class="font-semibold text-theme-text">{{ currentRoleName }}</span>.
              To continue, you need to switch to
              <span class="font-semibold text-primary-600">{{ targetRoleName }}</span>.
            </p>
          </div>

          <div class="flex gap-3 w-full mt-1">
            <button
              @click="showRoleSwitch = false"
              class="flex-1 py-2.5 rounded-xl border border-theme-border text-theme-text2 text-sm font-medium hover:bg-theme-bg2 transition"
            >
              Cancel
            </button>
            <button
              @click="switchRole"
              :disabled="isSwitching"
              class="flex-1 py-2.5 rounded-xl bg-primary-600 text-white text-sm font-semibold hover:bg-primary-500 transition disabled:opacity-60 flex items-center justify-center gap-2"
            >
              <i v-if="isSwitching" class="pi pi-spin pi-spinner text-xs"></i>
              Switch Role
            </button>
          </div>
        </div>
      </div>
    </transition>
  </teleport>
</template>

<style scoped>
/* Fade-slide for tab content */
.fade-slide-enter-active { transition: all 0.3s ease-out; }
.fade-slide-leave-active { transition: all 0.2s ease-in; }
.fade-slide-enter-from { opacity: 0; transform: translateY(10px); }
.fade-slide-leave-to { opacity: 0; transform: translateY(-6px); }

/* Role modal */
.role-modal-enter-active { transition: all 0.25s ease-out; }
.role-modal-leave-active { transition: all 0.2s ease-in; }
.role-modal-enter-from, .role-modal-leave-to { opacity: 0; }
</style>
